<template>
  <div class="app-inline-customer-selector full-width">
    <div class="app-inline-customer-selector__label-row">
      <span>{{ label }}</span>
      <button type="button" class="app-inline-customer-selector__link" @click="toggleInlineForm">
        {{ newButtonLabel }}
      </button>
    </div>

    <multiselect
      v-model="customerModel"
      :options="customerOptions"
      track-by="value"
      label="label"
      :show-labels="false"
      :searchable="true"
      :allow-empty="true"
      :loading="loadingCustomers"
      :placeholder="placeholder"
      :class="['app-modal-select', { 'is-invalid': hasError }]"
    >
      <template #noResult>
        <span>Sin opción disponible</span>
      </template>
      <template #noOptions>
        <span>Sin opción disponible</span>
      </template>
    </multiselect>

    <div class="invalid-feedback d-block" v-if="hasError">
      <span>{{ errorMessage }}</span>
    </div>

    <div v-if="showInlineForm" class="app-inline-customer-selector__inline">
      <label class="app-modal-field">
        <span>{{ inputLabel }}</span>
        <input
          v-model.trim="inlineCustomerName"
          type="text"
          class="form-control"
          :placeholder="inputPlaceholder"
        />
      </label>

      <div class="app-inline-customer-selector__actions">
        <button type="button" class="btn btn-light" @click="cancelInlineForm">Cancelar</button>
        <button
          type="button"
          class="app-header-btn app-header-btn--primary"
          :disabled="creatingInlineCustomer || inlineCustomerName === ''"
          @click="saveInlineCustomer"
        >
          Guardar y usar cliente
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import customersService from '@/services/customers';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';

export default {
  name: 'AppInlineCustomerSelector',
  props: {
    modelValue: {
      type: [Object, Number, String, null],
      default: null,
    },
    label: {
      type: String,
      default: 'Cliente *',
    },
    placeholder: {
      type: String,
      default: 'Buscar cliente',
    },
    newButtonLabel: {
      type: String,
      default: '+ Nuevo cliente',
    },
    inputLabel: {
      type: String,
      default: 'Nombre del cliente *',
    },
    inputPlaceholder: {
      type: String,
      default: 'Nombre del cliente',
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: 'Selecciona un cliente.',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      customerOptions: [],
      loadingCustomers: false,
      showInlineForm: false,
      inlineCustomerName: '',
      creatingInlineCustomer: false,
    };
  },
  computed: {
    customerModel: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value || null);
      },
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(nextValue) {
        const normalized = this.resolveExistingOption(nextValue);
        if (normalized && normalized !== nextValue) {
          this.$emit('update:modelValue', normalized);
        }
      },
    },
  },
  mounted() {
    this.loadCustomers();
  },
  methods: {
    resolveExistingOption(value) {
      const valueId = String(value?.value || value || '').trim();
      if (valueId === '') {
        return null;
      }

      return this.customerOptions.find((option) => String(option?.value || '') === valueId) || value;
    },
    async loadCustomers() {
      this.loadingCustomers = true;

      try {
        const response = await customersService.selectors();
        this.customerOptions = normalizeCatalogOptions(extractCollection(response));

        const selected = this.resolveExistingOption(this.modelValue);
        if (selected && selected !== this.modelValue) {
          this.$emit('update:modelValue', selected);
        }
      } catch (error) {
        this.customerOptions = [];
      } finally {
        this.loadingCustomers = false;
      }
    },
    toggleInlineForm() {
      this.showInlineForm = !this.showInlineForm;
      if (!this.showInlineForm) {
        this.inlineCustomerName = '';
      }
    },
    cancelInlineForm() {
      this.showInlineForm = false;
      this.inlineCustomerName = '';
    },
    async saveInlineCustomer() {
      const name = String(this.inlineCustomerName || '').trim();
      if (name === '' || this.creatingInlineCustomer) {
        return;
      }

      this.creatingInlineCustomer = true;

      try {
        const response = await customersService.store({ name });
        const customer = extractPayload(response);
        const createdOption = {
          value: customer?.id,
          label: String(customer?.name || name).trim(),
          meta: customer,
        };

        await this.loadCustomers();

        const selectedCustomer = this.resolveExistingOption(createdOption) || createdOption;
        this.$emit('update:modelValue', selectedCustomer);
        this.cancelInlineForm();
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || 'No fue posible crear el cliente.');
      } finally {
        this.creatingInlineCustomer = false;
      }
    },
  },
};
</script>

<style scoped>
.app-inline-customer-selector {
  grid-column: 1 / -1;
  display: grid;
  gap: 10px;
}

.app-inline-customer-selector__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.app-inline-customer-selector__label-row span {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}

.app-inline-customer-selector__link {
  border: 0;
  background: transparent;
  color: #0070cd;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
}

.app-inline-customer-selector__inline {
  border: 1px dashed rgba(153, 152, 153, 0.45);
  border-radius: 8px;
  padding: 12px;
  display: grid;
  gap: 10px;
}

.app-inline-customer-selector__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.app-inline-customer-selector__actions .app-header-btn {
  min-height: 38px;
  padding: 0 16px;
  border-radius: 7px;
}
</style>
